import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import L from 'leaflet';
import { POI, City } from '../services/poiService';
import UserLocationMarker from './UserLocationMarker';
import NewPOIMarker from './NewPOIMarker';
import { getReadableCategoryName } from '../utils/categoryUtils';

// Helper function to escape HTML entities to prevent XSS
// Using regex-based approach for common HTML entities
const escapeHtml = (text: string): string => {
  const htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;'
  };
  return text.replace(/[&<>"'/]/g, (char) => htmlEntities[char] || char);
};

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom POI marker icon - using a red marker for validated POIs
const poiIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 6.904 12.5 28.5 12.5 28.5s12.5-21.596 12.5-28.5C25 5.596 19.404 0 12.5 0z" fill="#ef4444"/>
      <circle cx="12.5" cy="12.5" r="7" fill="#ffffff"/>
      <circle cx="12.5" cy="12.5" r="4" fill="#ef4444"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41],
  shadowAnchor: [12, 41]
});

// Custom POI marker icon for PENDING validation status - using orange/yellow color
const pendingPOIIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 6.904 12.5 28.5 12.5 28.5s12.5-21.596 12.5-28.5C25 5.596 19.404 0 12.5 0z" fill="#f59e0b"/>
      <circle cx="12.5" cy="12.5" r="7" fill="#ffffff"/>
      <circle cx="12.5" cy="12.5" r="4" fill="#f59e0b"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41],
  shadowAnchor: [12, 41]
});

// Helper function to create a labeled marker icon
const createLabeledIcon = (label: string, isPending: boolean): L.DivIcon => {
  const color = isPending ? '#f59e0b' : '#ef4444';
  const escapedLabel = escapeHtml(label);
  return L.divIcon({
    html: `
      <div class="poi-marker-with-label">
        <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 6.904 12.5 28.5 12.5 28.5s12.5-21.596 12.5-28.5C25 5.596 19.404 0 12.5 0z" fill="${color}"/>
          <circle cx="12.5" cy="12.5" r="7" fill="#ffffff"/>
          <circle cx="12.5" cy="12.5" r="4" fill="${color}"/>
        </svg>
        <div class="poi-marker-label">${escapedLabel}</div>
      </div>
    `,
    className: 'poi-custom-marker',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });
};

// Helper function to get the appropriate icon based on POI validation status and zoom level
const getIconForPOI = (poi: POI, showLabel: boolean = false): L.Icon | L.DivIcon => {
  if (showLabel) {
    const isPending = poi.validation_status === 'PENDING';
    return createLabeledIcon(poi.name, isPending);
  }
  return poi.validation_status === 'PENDING' ? pendingPOIIcon : poiIcon;
};

// Custom city marker icon - using a blue marker with a star for cities
const cityIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="30" height="46" viewBox="0 0 30 46" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C8.373 0 3 5.373 3 12c0 8.284 12 34 12 34s12-25.716 12-34c0-6.627-5.373-12-12-12z" fill="#3b82f6"/>
      <circle cx="15" cy="12" r="8" fill="#ffffff"/>
      <path d="M15 6l1.545 4.755h5l-4.045 2.94 1.545 4.755L15 15.51l-4.045 2.94 1.545-4.755-4.045-2.94h5z" fill="#3b82f6"/>
    </svg>
  `),
  iconSize: [30, 46],
  iconAnchor: [15, 46],
  popupAnchor: [0, -46],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41],
  shadowAnchor: [12, 41]
});

// Custom cluster icon creation function for bubble-style clusters
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createClusterCustomIcon = (cluster: any) => {
  const count = cluster.getChildCount();
  
  // Define different color tiers based on marker count
  let colorClass = 'cluster-small';
  
  if (count >= 100) {
    colorClass = 'cluster-large';
  } else if (count >= 10) {
    colorClass = 'cluster-medium';
  }
  
  return L.divIcon({
    html: `<div class="custom-cluster-icon ${colorClass}"><span>${count}</span></div>`,
    className: 'custom-marker-cluster',
    iconSize: L.point(40, 40, true)
  });
};

interface UserLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

interface MapProps {
  center?: [number, number];
  zoom?: number;
  height?: string;
  className?: string;
  pois?: POI[];
  cities?: City[];
  userLocation?: UserLocation | null;
  centerOnUser?: boolean;
  centerOnSearch?: [number, number] | null;
  onPOIClick?: (poi: POI) => void;
  onPOIViewDetails?: (poi: POI) => void;
  onPOIAddCard?: (poi: POI) => void;
  onCityClick?: (city: City) => void;
  onMapClick?: (lat: number, lng: number) => void;
  onMapMove?: (lat: number, lng: number) => void;
  onZoomChange?: (zoom: number) => void;
  newPOILocation?: [number, number] | null;
  onNewPOIConfirm?: () => void;
  onNewPOICancel?: () => void;
  isAuthenticated?: boolean;
}

// Component for handling map clicks to add new POIs
const MapClickHandler = ({ 
  onMapClick, 
  pois 
}: { 
  onMapClick?: (lat: number, lng: number) => void;
  pois: POI[];
}) => {
  useMapEvents({
    click: (e) => {
      if (!onMapClick) return;
      
      const clickedLat = e.latlng.lat;
      const clickedLng = e.latlng.lng;
      
      // Check if click is near any existing POI (within ~50m radius)
      const nearbyPOI = pois.find(poi => {
        const poiLat = parseFloat(poi.latitude);
        const poiLng = parseFloat(poi.longitude);
        const distance = Math.sqrt(
          Math.pow((clickedLat - poiLat) * 111320, 2) + 
          Math.pow((clickedLng - poiLng) * 111320 * Math.cos(clickedLat * Math.PI / 180), 2)
        );
        return distance < 10; // 10 meters
      });
      
      // Only trigger onMapClick if no POI is nearby
      if (!nearbyPOI) {
        onMapClick(clickedLat, clickedLng);
      }
    },
  });
  return null;
};

// Component for handling map centering
const MapController = ({ 
  centerOnUser, 
  centerOnSearch, 
  userLocation 
}: { 
  centerOnUser?: boolean;
  centerOnSearch?: [number, number] | null;
  userLocation?: UserLocation | null;
}) => {
  const map = useMapEvents({});

  // Center on user location
  React.useEffect(() => {
    if (centerOnUser && userLocation) {
      map.setView([userLocation.latitude, userLocation.longitude], 15, {
        animate: true,
        duration: 0.5
      });
    }
  }, [centerOnUser, userLocation, map]);

  // Center on search result
  React.useEffect(() => {
    if (centerOnSearch) {
      map.setView(centerOnSearch, 15, {
        animate: true,
        duration: 0.8
      });
    }
  }, [centerOnSearch, map]);

  return null;
};

// Component for handling map movement to fetch new POIs
const MapMoveHandler = ({ 
  onMapMove 
}: { 
  onMapMove?: (lat: number, lng: number) => void;
}) => {
  const lastCenter = React.useRef<[number, number] | null>(null);
  const moveTimeoutRef = React.useRef<number | null>(null);

  const map = useMapEvents({
    moveend: () => {
      if (!onMapMove) return;

      const center = map.getCenter();
      const newCenter: [number, number] = [center.lat, center.lng];

      // Clear any existing timeout
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }

      // Debounce the move event to avoid excessive API calls
      moveTimeoutRef.current = window.setTimeout(() => {
        if (lastCenter.current) {
          // Calculate distance between last center and new center
          const [lastLat, lastLng] = lastCenter.current;
          const distance = Math.sqrt(
            Math.pow((newCenter[0] - lastLat) * 111320, 2) + 
            Math.pow((newCenter[1] - lastLng) * 111320 * Math.cos(newCenter[0] * Math.PI / 180), 2)
          );

          // Only trigger onMapMove if the user has moved the map significantly (>5km)
          if (distance > 5000) {
            console.log(`Map moved ${Math.round(distance)}m, fetching new POIs for location: ${newCenter[0]}, ${newCenter[1]}`);
            onMapMove(newCenter[0], newCenter[1]);
            lastCenter.current = newCenter;
          }
        } else {
          // First time, just set the center without triggering fetch
          lastCenter.current = newCenter;
        }
      }, 1000); // 1 second debounce
    }
  });

  // Update last center when map center changes programmatically
  React.useEffect(() => {
    const center = map.getCenter();
    lastCenter.current = [center.lat, center.lng];
  }, [map]);

  return null;
};

// Component for handling zoom changes
const ZoomHandler = ({ 
  onZoomChange 
}: { 
  onZoomChange?: (zoom: number) => void;
}) => {
  const map = useMapEvents({
    zoomend: () => {
      if (onZoomChange) {
        const currentZoom = map.getZoom();
        onZoomChange(currentZoom);
      }
    }
  });

  // Report initial zoom level
  React.useEffect(() => {
    if (onZoomChange) {
      onZoomChange(map.getZoom());
    }
  }, [map, onZoomChange]);

  return null;
};

const Map: React.FC<MapProps> = ({ 
  center = [40.7128, -74.0060], // Default to New York City
  zoom = 13,
  height = 'h-96', // Default responsive height
  className = '',
  pois = [],
  cities = [],
  userLocation = null,
  centerOnUser = false,
  centerOnSearch = null,
  onPOIClick,
  onPOIViewDetails,
  onPOIAddCard,
  onCityClick,
  onMapClick,
  onMapMove,
  onZoomChange,
  newPOILocation = null,
  onNewPOIConfirm,
  onNewPOICancel,
  isAuthenticated = false
}) => {
  // State to track current zoom level for showing/hiding labels
  const [currentZoom, setCurrentZoom] = useState<number>(zoom);
  
  // Threshold zoom level for showing labels (15 or higher)
  const LABEL_ZOOM_THRESHOLD = 15;
  
  // Sync internal zoom state with zoom prop changes
  useEffect(() => {
    setCurrentZoom(zoom);
  }, [zoom]);
  
  // Memoize whether labels should be shown based on current zoom
  const showLabels = useMemo(() => currentZoom >= LABEL_ZOOM_THRESHOLD, [currentZoom]);
  
  const handleMarkerClick = (poi: POI) => {
    if (onPOIClick) {
      onPOIClick(poi);
    }
  };

  const handleViewDetails = (poi: POI) => {
    if (onPOIViewDetails) {
      onPOIViewDetails(poi);
    }
  };

  const handleAddCard = (poi: POI) => {
    if (onPOIAddCard) {
      onPOIAddCard(poi);
    }
  };

  const handleCityClick = (city: City) => {
    if (onCityClick) {
      onCityClick(city);
    }
  };
  
  // Update internal zoom state
  const handleZoomChange = (newZoom: number) => {
    setCurrentZoom(newZoom);
    if (onZoomChange) {
      onZoomChange(newZoom);
    }
  };

  return (
    <div className={`relative ${className} ${height}`}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        className="rounded-lg shadow-lg z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Map click handler for adding new POIs */}
        <MapClickHandler onMapClick={onMapClick} pois={pois} />
        
        {/* Map controller for centering */}
        <MapController 
          centerOnUser={centerOnUser} 
          centerOnSearch={centerOnSearch}
          userLocation={userLocation}
        />
        
        {/* Map move handler for fetching new POIs */}
        <MapMoveHandler onMapMove={onMapMove} />
        
        {/* Zoom handler for tracking zoom level */}
        <ZoomHandler onZoomChange={handleZoomChange} />
        
        {/* User Location Marker */}
        {userLocation && (
          <UserLocationMarker
            latitude={userLocation.latitude}
            longitude={userLocation.longitude}
            accuracy={userLocation.accuracy}
            centerOnUser={centerOnUser}
          />
        )}
        
        {/* New POI Marker */}
        {newPOILocation && onNewPOIConfirm && onNewPOICancel && (
          <NewPOIMarker
            position={newPOILocation}
            onAddPOI={onNewPOIConfirm}
            onCancel={onNewPOICancel}
          />
        )}
        
        {/* POI Markers with Clustering */}
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          iconCreateFunction={createClusterCustomIcon}
        >
          {pois.map((poi) => (
            <Marker
              key={poi.id}
              position={[parseFloat(poi.latitude), parseFloat(poi.longitude)]}
              icon={getIconForPOI(poi, showLabels)}
              eventHandlers={{
                click: () => handleMarkerClick(poi),
              }}
            >
              <Popup>
                <div className="text-center min-w-[200px]">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">{poi.name}</h3>
                  {poi.category && (
                    <div className="mb-2">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                        {getReadableCategoryName(poi.category)}
                      </span>
                    </div>
                  )}
                  {poi.validation_status === 'PENDING' && (
                    <div className="mb-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      PENDING
                    </div>
                  )}
                  <p className="text-xs text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-3">
                    {parseFloat(poi.latitude).toFixed(4)}, {parseFloat(poi.longitude).toFixed(4)}
                  </p>
                  <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(poi);
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors min-h-[44px] flex items-center justify-center font-medium"
                    >
                      View Details
                    </button>
                    {isAuthenticated && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddCard(poi);
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition-colors min-h-[44px] flex items-center justify-center font-medium"
                      >
                        Add Card
                      </button>
                    )}
                    {!isAuthenticated && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1 text-center">
                        Sign in to add cards
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
        
        {/* City Markers - shown separately, not clustered */}
        {cities.map((city) => (
          <Marker
            key={`${city.city}-${city.country}`}
            position={[city.latitude, city.longitude]}
            icon={cityIcon}
          >
            <Popup>
              <div className="text-center min-w-[200px]">
                <h3 className="font-bold text-blue-900 mb-2 text-base">{city.city}</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{city.country}</p>
                <div className="mb-2">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-medium">
                    {city.poi_count} {city.poi_count === 1 ? 'place' : 'places'}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCityClick(city);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium transition-colors"
                >
                  Click here to explore this city
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default Map;
